import {
  OPENPENCIL_COMMAND_IDS,
  OPENPENCIL_FACT_MAP_SCHEMA,
  OPENPENCIL_FIGMA_SOURCE_BUNDLE_SCHEMA,
  OpenPencilCliReadbackSchema,
  OpenPencilFigmaSourceBundleSchema,
  OpenPencilSourceProfileSchema,
  OpenPencilSourceRevisionLineageSchema,
  OpenPencilSurfacePreviewManifestSchema,
  REQUIRED_OPENPENCIL_FACT_KINDS_BY_PROFILE,
  WON_BANKING_OPENPENCIL_CANARY_METRICS,
  type BuildOpenPencilBlockedInput,
  type BuildOpenPencilSourceBundleInput,
  type OpenPencilCliReadback,
  type OpenPencilFactKind,
  type OpenPencilFigmaSourceBundle,
  type OpenPencilOntologyFactMapItem,
  type OpenPencilSidecarRef,
  type OpenPencilSourceFacts,
  type OpenPencilSourceGate,
  type OpenPencilSourceProfile,
  type OpenPencilSurfacePreviewManifest,
} from "./contracts.ts";

const DEFAULT_SOURCE_ID = "openpencil-figma-source";
const DEFAULT_SOURCE_NAME = "Figma .fig source";
const DEFAULT_FILE_NAME = "source.fig";

const SIDECAR_FILE_NAMES = {
  readback: "openpencil-cli-readback.v2.json",
  rawReadback: "openpencil-cli-raw-readback.v2.json",
  factMap: "openpencil-source-fact-map.v2.json",
  sourceFacts: "openpencil-source-facts.v2.json",
  previewManifest: "openpencil-surface-preview-manifest.v1.json",
  claimBoundary: "openpencil-claim-boundary.v2.json",
  bundle: "openpencil-figma-source-bundle.v2.json",
  blockedEvidence: "openpencil-blocked-evidence.v2.json",
} as const;

function sourceProfileOf(input: {
  sourceProfile?: unknown;
}): OpenPencilSourceProfile | null {
  if (input.sourceProfile === undefined) return "application-story";
  const parsed = OpenPencilSourceProfileSchema.safeParse(input.sourceProfile);
  return parsed.success ? parsed.data : null;
}

const INVALID_SOURCE_PROFILE_REASON =
  "Invalid OpenPencil source profile. Expected application-story or design-library.";

function safeId(value: string): string {
  const normalized = value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return normalized || DEFAULT_SOURCE_ID;
}

function publicRef(value: string | undefined): string | undefined {
  if (!value) return undefined;
  if (value.startsWith("/") || value.startsWith("file:")) return undefined;
  if (/^[a-zA-Z]:[\\/]/.test(value)) return undefined;
  return value;
}

function buildSidecars(
  refs: Partial<Record<string, OpenPencilSidecarRef>> | undefined,
  status: "written" | "blocked",
  previewReady = false,
): Record<string, OpenPencilSidecarRef> {
  return Object.fromEntries(
    Object.entries(SIDECAR_FILE_NAMES).map(([key, fileName]) => {
      const supplied = refs?.[key];
      return [
        key,
        {
          fileName,
          status:
            key === "blockedEvidence" && status === "written"
              ? "pending"
              : key === "previewManifest" && status === "written"
                ? previewReady
                  ? (supplied?.status ?? "written")
                  : "pending"
                : (supplied?.status ?? status),
          ...(publicRef(supplied?.artifactRef)
            ? { artifactRef: publicRef(supplied?.artifactRef) }
            : {}),
          ...(supplied?.bytes !== undefined ? { bytes: supplied.bytes } : {}),
          ...(supplied?.sha256 ? { sha256: supplied.sha256 } : {}),
        },
      ];
    }),
  );
}

function buildClaimBoundary() {
  return {
    accepted: [
      "OpenPencil readback is verified source evidence for Studio planning.",
      "Verified source facts may be mapped to pages, stories, and design-token decisions.",
    ],
    notClaimed: [
      "OpenPencil readback is not final Coogo JSON.",
      "Frame geometry does not prove story completeness or component semantics.",
      "Visual semantics require a bounded judge or an explicit human decision.",
    ],
    hotPathBans: [
      "Do not parse raw .fig files in a renderer or preview hot path.",
      "Do not invoke OpenPencil or a model while rendering Coogo JSON.",
    ],
    nextProofs: [
      "Page and story mapping approval",
      "Bounded visual semantic judgement",
      "Selected source-facts assembly and runtime proof",
    ],
  };
}

function generationAdmission(generatedAt: string, previewReady = false) {
  return {
    status: "blocked" as const,
    reasonCodes: [
      ...(!previewReady ? ["visual_preview_pending"] : []),
      "source_mapping_pending",
      "visual_judgement_pending",
    ],
    requiredGateIds: [
      ...(!previewReady ? ["surface-preview-evidence"] : []),
      "source-mapping-approved",
      "visual-semantics-approved",
      "application-assembly-validated",
      "runtime-proof-passed",
    ],
    evaluatedAt: generatedAt,
  };
}

function normalizePreviewManifest(
  value: unknown,
  readback: OpenPencilCliReadback,
): OpenPencilSurfacePreviewManifest | null {
  const parsed = OpenPencilSurfacePreviewManifestSchema.safeParse(value);
  if (!parsed.success) return null;
  const facts = new Map(
    readback.query.surfaceSamples.map((surface) => [surface.id, surface]),
  );
  let expectedFacts = facts;
  if (parsed.data.scope) {
    const selectedFacts = new Map<
      string,
      OpenPencilCliReadback["query"]["surfaceSamples"][number]
    >();
    for (const sourceNodeId of parsed.data.scope.selectedSourceNodeIds) {
      const matches = readback.query.surfaceSamples.filter(
        (surface) => surface.sourceNodeId === sourceNodeId,
      );
      if (matches.length !== 1) return null;
      const match = matches[0]!;
      selectedFacts.set(match.id, match);
    }
    if (
      selectedFacts.size !== parsed.data.scope.selectedSourceNodeIds.length
    ) {
      return null;
    }
    expectedFacts = selectedFacts;
  }
  const seen = new Set<string>();
  for (const item of parsed.data.items) {
    const fact = expectedFacts.get(item.surfaceId);
    if (
      !fact ||
      seen.has(item.surfaceId) ||
      item.pageId !== fact.pageId ||
      item.nodeId !== fact.nodeId ||
      item.sourceWidth !== fact.width ||
      item.sourceHeight !== fact.height ||
      publicRef(item.artifactRef) !== item.artifactRef
    ) {
      return null;
    }
    seen.add(item.surfaceId);
  }
  for (const failure of parsed.data.failures) {
    const fact = expectedFacts.get(failure.surfaceId);
    if (
      !fact ||
      seen.has(failure.surfaceId) ||
      failure.pageId !== fact.pageId ||
      failure.nodeId !== fact.nodeId
    ) {
      return null;
    }
    seen.add(failure.surfaceId);
  }
  if (
    parsed.data.requested !== expectedFacts.size ||
    parsed.data.rendered !== parsed.data.items.length ||
    parsed.data.failed !== parsed.data.failures.length ||
    parsed.data.rendered + parsed.data.failed !== parsed.data.requested ||
    parsed.data.totalBytes !==
      parsed.data.items.reduce((total, item) => total + item.bytes, 0) ||
    seen.size !== expectedFacts.size
  ) {
    return null;
  }
  return parsed.data;
}

function previewGate(
  manifest: OpenPencilSurfacePreviewManifest | null,
): OpenPencilSourceGate {
  if (manifest?.scope?.kind === "selected-source-node-ids-v1") {
    return {
      id: "surface-preview-evidence",
      scope: "generation",
      status: "blocked",
      message:
        "Bounded preview coverage cannot establish complete generation preview evidence.",
      evidenceIds: ["openpencil-surface-preview-manifest.v1.json"],
    };
  }
  const complete = Boolean(
    manifest &&
    manifest.requested > 0 &&
    manifest.rendered === manifest.requested &&
    manifest.failed === 0 &&
    manifest.failures.length === 0,
  );
  return {
    id: "surface-preview-evidence",
    scope: "generation",
    status: complete ? "pass" : "blocked",
    message: complete
      ? "Every surface candidate has a hashed OpenPencil PNG preview artifact."
      : "Surface preview evidence is missing, partial, failed, or inconsistent.",
    evidenceIds: ["openpencil-surface-preview-manifest.v1.json"],
  };
}

function buildSourceFacts(
  input: Required<
    Pick<
      BuildOpenPencilSourceBundleInput,
      "sourceId" | "sourceName" | "fileName" | "generatedAt"
    >
  > &
    BuildOpenPencilSourceBundleInput,
  readback: OpenPencilCliReadback,
): OpenPencilSourceFacts {
  return {
    documentProfile: {
      kind: "source-document-profile",
      sourceProfile: input.sourceProfile ?? "application-story",
      sourceId: input.sourceId,
      sourceName: input.sourceName,
      fileName: input.fileName,
      ...(publicRef(input.sourceArtifactRef)
        ? { sourceArtifactRef: publicRef(input.sourceArtifactRef) }
        : {}),
      pages: readback.info.pages,
      totalNodes: readback.info.totalNodes,
      sections: readback.query.sections,
      frameCandidates: readback.query.frameCandidates,
      mobileSizedFrames: readback.query.mobileSizedFrames,
      nonMobileFrames: readback.query.nonMobileFrames,
      variableCollections: readback.variables.declaredCollections,
      observedVariableCollections: readback.variables.observedCollections,
      variables: readback.variables.variables,
      detectedColors: readback.analyzeColors.detectedColors,
      generatedAt: input.generatedAt,
    },
    pages: readback.pages.samples,
    storySections: readback.query.sectionSamples,
    surfaceCandidates: readback.query.surfaceSamples,
    variableCollections: readback.variables.collectionSamples ?? [],
    designTokens: readback.variables.samples,
    visualTokenEvidence: readback.analyzeColors.samples,
    ...(readback.uiOntology ? { uiOntology: readback.uiOntology } : {}),
  };
}

function fact(
  kind: OpenPencilFactKind,
  id: string,
  label: string,
  sourceLocator: string,
  confidence: number,
  evidence: OpenPencilOntologyFactMapItem["evidence"],
): OpenPencilOntologyFactMapItem {
  return { kind, id, label, sourceLocator, confidence, evidence };
}

function buildFactMap(sourceFacts: OpenPencilSourceFacts) {
  const profile = sourceFacts.documentProfile;
  return [
    fact(
      "source-document-profile",
      `${profile.sourceId}:profile`,
      profile.sourceName,
      "readback.info",
      1,
      {
        pages: profile.pages,
        totalNodes: profile.totalNodes,
        sections: profile.sections,
        frameCandidates: profile.frameCandidates,
        mobileSizedFrames: profile.mobileSizedFrames,
        variables: profile.variables,
      },
    ),
    ...sourceFacts.pages.map((page, index) =>
      fact(
        "source-page",
        page.id,
        page.name,
        `readback.pages.samples.${index}`,
        page.confidence,
        {
          nodeId: page.nodeId,
          sourceNodeId: page.sourceNodeId ?? null,
          ordinal: page.ordinal,
          nodeCount: page.nodeCount,
        },
      ),
    ),
    ...sourceFacts.storySections.map((section, index) =>
      fact(
        "story-section",
        section.id,
        section.name,
        `readback.query.sectionSamples.${index}`,
        section.confidence,
        {
          pageId: section.pageId,
          pageName: section.pageName,
          nodeId: section.nodeId,
          sourceNodeId: section.sourceNodeId ?? null,
          childCount: section.childCount,
        },
      ),
    ),
    ...sourceFacts.surfaceCandidates.map((surface, index) =>
      fact(
        "surface-candidate",
        surface.id,
        surface.name,
        `readback.query.surfaceSamples.${index}`,
        surface.confidence,
        {
          pageId: surface.pageId,
          pageName: surface.pageName,
          nodeId: surface.nodeId,
          sourceNodeId: surface.sourceNodeId ?? null,
          width: surface.width,
          height: surface.height,
          classification: surface.classification,
        },
      ),
    ),
    ...sourceFacts.designTokens.map((token) =>
      fact(
        "design-token",
        token.id,
        token.name,
        token.sourceLocator,
        token.confidence,
        {
          collectionId: token.collectionId,
          collectionName: token.collectionName,
          tokenType: token.tokenType,
          value: token.value ?? null,
          defaultModeId: token.defaultModeId ?? null,
          modeCount: token.modes.length,
          modeValueCount: token.valuesByMode.length,
          aliasValueCount: token.valuesByMode.filter((value) => value.aliasId)
            .length,
          sourceKey: token.sourceKey ?? null,
          sourceVersion: token.sourceVersion ?? null,
        },
      ),
    ),
    ...sourceFacts.visualTokenEvidence.map((evidence) =>
      fact(
        "visual-token-evidence",
        evidence.id,
        evidence.label,
        evidence.sourceLocator,
        evidence.confidence,
        {
          tokenId: evidence.tokenId ?? null,
          value: evidence.value,
          usageCount: evidence.usageCount,
        },
      ),
    ),
  ];
}

function commandGate(readback: OpenPencilCliReadback): OpenPencilSourceGate {
  const byId = new Map(
    readback.commandEvidence.map((item) => [item.commandId, item]),
  );
  const complete =
    byId.size === OPENPENCIL_COMMAND_IDS.length &&
    OPENPENCIL_COMMAND_IDS.every((id) => {
      const item = byId.get(id);
      return (
        item?.status === "ok" &&
        item.exitCode === 0 &&
        typeof item.durationMs === "number" &&
        Boolean(item.toolVersion) &&
        Boolean(item.runnerVersion)
      );
    });
  return {
    id: "openpencil-command-evidence",
    scope: "source-readback",
    status: complete ? "pass" : "blocked",
    message: complete
      ? "All required OpenPencil commands produced versioned execution evidence."
      : "Required OpenPencil command evidence is missing, duplicated, failed, or unversioned.",
    evidenceIds: ["openpencil-cli-readback.v2.json"],
  };
}

function variableEvidenceConsistent(readback: OpenPencilCliReadback): boolean {
  const {
    declaredCollections,
    observedCollections,
    variables,
    collectionSamples,
    samples,
  } = readback.variables;
  if (collectionSamples === undefined) {
    return (
      declaredCollections === 0 &&
      observedCollections === 0 &&
      variables === 0 &&
      samples.length === 0
    );
  }

  const collectionsById = new Map(
    collectionSamples.map((collection) => [collection.id, collection]),
  );
  const variablesById = new Map(samples.map((token) => [token.id, token]));
  if (
    declaredCollections !== collectionSamples.length ||
    collectionsById.size !== collectionSamples.length ||
    observedCollections !==
      collectionSamples.filter((collection) => collection.variableCount > 0)
        .length ||
    variables !== samples.length ||
    variablesById.size !== samples.length ||
    variables !==
      collectionSamples.reduce(
        (total, collection) => total + collection.variableCount,
        0,
      )
  ) {
    return false;
  }

  const membersByCollection = new Map(
    collectionSamples.map((collection) => [collection.id, 0]),
  );
  for (const token of samples) {
    const collection = collectionsById.get(token.collectionId);
    if (
      !collection ||
      token.collectionName !== collection.name ||
      token.defaultModeId !== collection.defaultModeId
    ) {
      return false;
    }

    const collectionModes = new Map(
      collection.modes.map((mode) => [mode.modeId, mode]),
    );
    const tokenModes = new Map(token.modes.map((mode) => [mode.modeId, mode]));
    const valuesByMode = new Map(
      token.valuesByMode.map((entry) => [entry.modeId, entry]),
    );
    const modesMatch =
      collectionModes.size === collection.modes.length &&
      tokenModes.size === token.modes.length &&
      valuesByMode.size === token.valuesByMode.length &&
      token.modes.length === collection.modes.length &&
      token.valuesByMode.length === collection.modes.length &&
      collection.modes.every((mode) => {
        const tokenMode = tokenModes.get(mode.modeId);
        const value = valuesByMode.get(mode.modeId);
        return (
          tokenMode?.name === mode.name &&
          tokenMode.isDefault === mode.isDefault &&
          value?.modeName === mode.name
        );
      });
    if (!modesMatch) return false;

    const aliasesResolve = token.valuesByMode.every((value) => {
      if (value.aliasId === undefined) return true;
      const target = variablesById.get(value.aliasId);
      return (
        target !== undefined &&
        token.tokenType !== "unknown" &&
        target.tokenType === token.tokenType
      );
    });
    if (!aliasesResolve) return false;

    membersByCollection.set(
      collection.id,
      (membersByCollection.get(collection.id) ?? 0) + 1,
    );
  }

  return collectionSamples.every(
    (collection) =>
      membersByCollection.get(collection.id) === collection.variableCount,
  );
}

function countConsistencyGate(
  readback: OpenPencilCliReadback,
): OpenPencilSourceGate {
  const consistent =
    readback.info.pages > 0 &&
    readback.info.totalNodes > 0 &&
    readback.pages.count === readback.info.pages &&
    readback.pages.samples.length === readback.pages.count &&
    readback.query.sections === readback.query.sectionSamples.length &&
    readback.query.frameCandidates === readback.query.surfaceSamples.length &&
    readback.query.mobileSizedFrames + readback.query.nonMobileFrames ===
      readback.query.frameCandidates &&
    variableEvidenceConsistent(readback) &&
    readback.analyzeColors.detectedColors ===
      readback.analyzeColors.samples.length;
  return {
    id: "source-count-consistency",
    scope: "source-readback",
    status: consistent ? "pass" : "blocked",
    message: consistent
      ? "Declared source counts, variable memberships, modes, and aliases match retained evidence."
      : "Declared source counts, variable memberships, modes, or aliases do not match retained evidence; synthetic placeholders are forbidden.",
    evidenceIds: ["openpencil-cli-readback.v2.json"],
  };
}

function factCoverageGate(
  sourceProfile: OpenPencilSourceProfile,
  factMap: OpenPencilOntologyFactMapItem[],
): OpenPencilSourceGate {
  const kinds = new Set(factMap.map((item) => item.kind));
  const complete = REQUIRED_OPENPENCIL_FACT_KINDS_BY_PROFILE[
    sourceProfile
  ].every((kind) => kinds.has(kind));
  return {
    id: "source-fact-coverage",
    scope: "source-readback",
    status: complete ? "pass" : "blocked",
    message: complete
      ? "Every required source-fact kind is backed by retained evidence."
      : "One or more required source-fact kinds have no evidence.",
    evidenceIds: ["openpencil-source-fact-map.v2.json"],
  };
}

function designLibraryUiOntologyGate(
  readback: OpenPencilCliReadback | null,
): OpenPencilSourceGate {
  const complete =
    (readback?.uiOntology?.counts.componentSets ?? 0) > 0 &&
    (readback?.uiOntology?.counts.components ?? 0) > 0;
  return {
    id: "design-library-ui-ontology",
    scope: "source-readback",
    status: complete ? "pass" : "blocked",
    message: complete
      ? "Design-library source readback includes component-set and component evidence."
      : "Design-library source readback requires real component-set and component evidence.",
    evidenceIds: ["openpencil-cli-readback.v2.json"],
  };
}

function locatorCoverageGate(
  sourceFacts: OpenPencilSourceFacts,
): OpenPencilSourceGate {
  const complete =
    sourceFacts.pages.every((item) => item.nodeId && item.name) &&
    sourceFacts.storySections.every(
      (item) => item.nodeId && item.pageId && item.pageName,
    ) &&
    sourceFacts.surfaceCandidates.every(
      (item) => item.nodeId && item.pageId && item.pageName,
    ) &&
    sourceFacts.designTokens.every((item) => item.sourceLocator) &&
    sourceFacts.visualTokenEvidence.every((item) => item.sourceLocator);
  return {
    id: "source-locator-coverage",
    scope: "source-readback",
    status: complete ? "pass" : "blocked",
    message: complete
      ? "All actionable facts retain source locators and page ancestry."
      : "Actionable source facts are missing locators or page ancestry.",
    evidenceIds: ["openpencil-source-facts.v2.json"],
  };
}

function generationGate(): OpenPencilSourceGate {
  return {
    id: "cogo-json-generation-admission",
    scope: "generation",
    status: "blocked",
    message: "Source readback alone never admits Coogo JSON generation.",
    evidenceIds: ["openpencil-claim-boundary.v2.json"],
  };
}

export function buildOpenPencilFigmaSourceBundle(
  input: BuildOpenPencilSourceBundleInput,
): OpenPencilFigmaSourceBundle {
  const generatedAt = input.generatedAt ?? new Date().toISOString();
  const sourceProfile = sourceProfileOf(input);
  const sourceName = input.sourceName?.trim() || DEFAULT_SOURCE_NAME;
  const sourceId =
    input.sourceId?.trim() || safeId(`${sourceName}-${input.fileSha256 ?? ""}`);
  const fileName = input.fileName?.trim() || DEFAULT_FILE_NAME;
  if (!sourceProfile) {
    return buildBlockedOpenPencilFigmaSourceBundle({
      ...input,
      sourceProfile: undefined,
      sourceId,
      sourceName,
      fileName,
      generatedAt,
      reason: INVALID_SOURCE_PROFILE_REASON,
    });
  }
  const readbackResult = OpenPencilCliReadbackSchema.safeParse(input.readback);
  if (!readbackResult.success) {
    return buildBlockedOpenPencilFigmaSourceBundle({
      ...input,
      sourceProfile,
      sourceId,
      sourceName,
      fileName,
      generatedAt,
      reason: `Invalid OpenPencil readback: ${readbackResult.error.issues
        .slice(0, 5)
        .map((issue) => `${issue.path.join(".") || "payload"} ${issue.message}`)
        .join("; ")}`,
    });
  }

  if (readbackResult.data.analysisProfile === "ontology") {
    return buildBlockedOpenPencilFigmaSourceBundle({
      ...input,
      sourceProfile,
      sourceId,
      sourceName,
      fileName,
      generatedAt,
      reason:
        "Ontology-profile readback cannot populate the generic source bundle because layout, query, and global color measurements were skipped.",
    });
  }

  const readback: OpenPencilCliReadback = {
    ...readbackResult.data,
    ...(publicRef(readbackResult.data.sourceArtifactRef)
      ? { sourceArtifactRef: publicRef(readbackResult.data.sourceArtifactRef) }
      : { sourceArtifactRef: undefined }),
    ...(publicRef(readbackResult.data.rawArtifactRef)
      ? { rawArtifactRef: publicRef(readbackResult.data.rawArtifactRef) }
      : { rawArtifactRef: undefined }),
  };

  const sourceFacts = buildSourceFacts(
    {
      ...input,
      sourceProfile,
      sourceId,
      sourceName,
      fileName,
      generatedAt,
    },
    readback,
  );
  const visualPreviewManifest = normalizePreviewManifest(
    input.visualPreviewManifest,
    readback,
  );
  const factMap = buildFactMap(sourceFacts);
  const gates = [
    commandGate(readback),
    countConsistencyGate(readback),
    ...(sourceProfile === "design-library"
      ? [designLibraryUiOntologyGate(readback)]
      : []),
    factCoverageGate(sourceProfile, factMap),
    locatorCoverageGate(sourceFacts),
    previewGate(visualPreviewManifest),
    generationGate(),
  ];
  const status = gates
    .filter((gate) => gate.scope === "source-readback")
    .every((gate) => gate.status === "pass")
    ? "ready"
    : "blocked";

  const bundle: OpenPencilFigmaSourceBundle = {
    schemaVersion: OPENPENCIL_FIGMA_SOURCE_BUNDLE_SCHEMA,
    status,
    sourceProfile,
    sourceId,
    sourceName,
    fileName,
    ...(input.fileSha256 ? { fileSha256: input.fileSha256 } : {}),
    ...(publicRef(input.sourceArtifactRef)
      ? { sourceArtifactRef: publicRef(input.sourceArtifactRef) }
      : {}),
    ...(publicRef(input.workspaceRef)
      ? { workspaceRef: publicRef(input.workspaceRef) }
      : {}),
    ...(input.revisionLineage && publicRef(input.revisionLineage.evidenceRef)
      ? {
          revisionLineage: {
            ...input.revisionLineage,
            evidenceRef: publicRef(input.revisionLineage.evidenceRef)!,
          },
        }
      : {}),
    generatedAt,
    readback,
    factMapSchemaVersion: OPENPENCIL_FACT_MAP_SCHEMA,
    factMap,
    sourceFacts,
    visualPreviewManifest,
    sidecars: buildSidecars(
      input.sidecarRefs,
      "written",
      visualPreviewManifest !== null,
    ),
    claimBoundary: buildClaimBoundary(),
    gates,
    generationAdmission: generationAdmission(
      generatedAt,
      gates.some(
        (gate) =>
          gate.id === "surface-preview-evidence" && gate.status === "pass",
      ),
    ),
    ...(status === "blocked"
      ? {
          blockedEvidence: {
            reason: gates
              .filter(
                (gate) =>
                  gate.scope === "source-readback" && gate.status === "blocked",
              )
              .map((gate) => gate.message)
              .join(" "),
            generatedAt,
          },
        }
      : {}),
  };
  return OpenPencilFigmaSourceBundleSchema.parse(bundle);
}

export function buildBlockedOpenPencilFigmaSourceBundle(
  input: BuildOpenPencilBlockedInput,
): OpenPencilFigmaSourceBundle {
  const generatedAt = input.generatedAt ?? new Date().toISOString();
  const validSourceProfile = sourceProfileOf(input);
  const sourceProfile = validSourceProfile ?? "application-story";
  const reason = validSourceProfile
    ? input.reason
    : `${input.reason} ${INVALID_SOURCE_PROFILE_REASON}`;
  const sourceName = input.sourceName?.trim() || DEFAULT_SOURCE_NAME;
  const sourceId =
    input.sourceId?.trim() || safeId(`${sourceName}-${input.fileSha256 ?? ""}`);
  const fileName = input.fileName?.trim() || DEFAULT_FILE_NAME;
  return OpenPencilFigmaSourceBundleSchema.parse({
    schemaVersion: OPENPENCIL_FIGMA_SOURCE_BUNDLE_SCHEMA,
    status: "blocked",
    sourceProfile,
    sourceId,
    sourceName,
    fileName,
    ...(input.fileSha256 ? { fileSha256: input.fileSha256 } : {}),
    ...(publicRef(input.sourceArtifactRef)
      ? { sourceArtifactRef: publicRef(input.sourceArtifactRef) }
      : {}),
    ...(publicRef(input.workspaceRef)
      ? { workspaceRef: publicRef(input.workspaceRef) }
      : {}),
    ...(input.revisionLineage && publicRef(input.revisionLineage.evidenceRef)
      ? {
          revisionLineage: {
            ...input.revisionLineage,
            evidenceRef: publicRef(input.revisionLineage.evidenceRef)!,
          },
        }
      : {}),
    generatedAt,
    readback: null,
    factMapSchemaVersion: OPENPENCIL_FACT_MAP_SCHEMA,
    factMap: [],
    sourceFacts: null,
    visualPreviewManifest: null,
    sidecars: buildSidecars(input.sidecarRefs, "blocked"),
    claimBoundary: buildClaimBoundary(),
    gates: [
      {
        id: "openpencil-command-evidence",
        scope: "source-readback",
        status: "blocked",
        message: reason,
        evidenceIds: ["openpencil-blocked-evidence.v2.json"],
      },
      ...(sourceProfile === "design-library"
        ? [designLibraryUiOntologyGate(null)]
        : []),
      generationGate(),
    ],
    generationAdmission: generationAdmission(generatedAt),
    blockedEvidence: {
      reason,
      ...(input.commandId ? { commandId: input.commandId } : {}),
      ...(input.exitCode !== undefined ? { exitCode: input.exitCode } : {}),
      ...(input.stderr ? { stderr: input.stderr.slice(0, 8000) } : {}),
      generatedAt,
    },
  });
}

export function normalizeOpenPencilFigmaSourceBundle(
  input: unknown,
): OpenPencilFigmaSourceBundle {
  const parsed = OpenPencilFigmaSourceBundleSchema.safeParse(input);
  if (parsed.success) {
    if (!parsed.data.readback) {
      return buildBlockedOpenPencilFigmaSourceBundle({
        sourceProfile: parsed.data.sourceProfile,
        sourceId: parsed.data.sourceId,
        sourceName: parsed.data.sourceName,
        fileName: parsed.data.fileName,
        fileSha256: parsed.data.fileSha256,
        sourceArtifactRef: parsed.data.sourceArtifactRef,
        workspaceRef: parsed.data.workspaceRef,
        revisionLineage: parsed.data.revisionLineage,
        generatedAt: parsed.data.generatedAt,
        reason:
          parsed.data.blockedEvidence?.reason ??
          "OpenPencil bundle contains no readback evidence.",
        sidecarRefs: parsed.data.sidecars,
      });
    }
    // Never trust serialized status/gates. Recompute all derived claims.
    return buildOpenPencilFigmaSourceBundle({
      sourceProfile: parsed.data.sourceProfile,
      sourceId: parsed.data.sourceId,
      sourceName: parsed.data.sourceName,
      fileName: parsed.data.fileName,
      fileSha256: parsed.data.fileSha256,
      sourceArtifactRef: parsed.data.sourceArtifactRef,
      workspaceRef: parsed.data.workspaceRef,
      revisionLineage: parsed.data.revisionLineage,
      generatedAt: parsed.data.generatedAt,
      readback: parsed.data.readback,
      visualPreviewManifest: parsed.data.visualPreviewManifest,
      sidecarRefs: parsed.data.sidecars,
    });
  }

  if (input && typeof input === "object" && !Array.isArray(input)) {
    const record = input as Record<string, unknown>;
    const revisionLineage = OpenPencilSourceRevisionLineageSchema.safeParse(
      record.revisionLineage,
    );
    const sourceProfile = sourceProfileOf(record);
    if (!sourceProfile) {
      return buildBlockedOpenPencilFigmaSourceBundle({
        sourceId:
          typeof record.sourceId === "string" ? record.sourceId : undefined,
        sourceName:
          typeof record.sourceName === "string"
            ? record.sourceName
            : undefined,
        fileName:
          typeof record.fileName === "string" ? record.fileName : undefined,
        fileSha256:
          typeof record.fileSha256 === "string"
            ? record.fileSha256
            : undefined,
        sourceArtifactRef:
          typeof record.sourceArtifactRef === "string"
            ? record.sourceArtifactRef
            : undefined,
        workspaceRef:
          typeof record.workspaceRef === "string"
            ? record.workspaceRef
            : undefined,
        revisionLineage: revisionLineage.success
          ? revisionLineage.data
          : undefined,
        generatedAt:
          typeof record.generatedAt === "string"
            ? record.generatedAt
            : undefined,
        reason: INVALID_SOURCE_PROFILE_REASON,
      });
    }
    return buildOpenPencilFigmaSourceBundle({
      sourceProfile,
      sourceId:
        typeof record.sourceId === "string" ? record.sourceId : undefined,
      sourceName:
        typeof record.sourceName === "string" ? record.sourceName : undefined,
      fileName:
        typeof record.fileName === "string" ? record.fileName : undefined,
      fileSha256:
        typeof record.fileSha256 === "string" ? record.fileSha256 : undefined,
      sourceArtifactRef:
        typeof record.sourceArtifactRef === "string"
          ? record.sourceArtifactRef
          : undefined,
      workspaceRef:
        typeof record.workspaceRef === "string"
          ? record.workspaceRef
          : undefined,
      revisionLineage: revisionLineage.success
        ? revisionLineage.data
        : undefined,
      generatedAt:
        typeof record.generatedAt === "string" ? record.generatedAt : undefined,
      readback: record.readback,
      visualPreviewManifest: record.visualPreviewManifest,
    });
  }
  return buildBlockedOpenPencilFigmaSourceBundle({
    reason: "Invalid OpenPencil source bundle payload.",
  });
}

export function summarizeOpenPencilFigmaSource(
  bundle: OpenPencilFigmaSourceBundle | null,
) {
  const profile = bundle?.sourceFacts?.documentProfile;
  return {
    status: bundle?.status ?? "blocked",
    generationAdmission: bundle?.generationAdmission.status ?? "blocked",
    sourceId: bundle?.sourceId ?? null,
    sourceName: bundle?.sourceName ?? null,
    pages: profile?.pages ?? 0,
    totalNodes: profile?.totalNodes ?? 0,
    sections: profile?.sections ?? 0,
    frameCandidates: profile?.frameCandidates ?? 0,
    mobileSizedFrames: profile?.mobileSizedFrames ?? 0,
    nonMobileFrames: profile?.nonMobileFrames ?? 0,
    variableCollections: profile?.variableCollections ?? 0,
    variables: profile?.variables ?? 0,
    detectedColors: profile?.detectedColors ?? 0,
  };
}

export function isWonBankingOpenPencilCanary(
  bundle: OpenPencilFigmaSourceBundle | null,
): boolean {
  const summary = summarizeOpenPencilFigmaSource(bundle);
  return Object.entries(WON_BANKING_OPENPENCIL_CANARY_METRICS).every(
    ([key, value]) => summary[key as keyof typeof summary] === value,
  );
}
